import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { 
  Shield, Users, FileWarning, Heart, LogOut, 
  TrendingUp, Bot, Trash2, RefreshCw, Search,
  User, MapPin, AlertCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const AdminSeedProfiles = () => {
  const navigate = useNavigate();
  const [seedProfiles, setSeedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [generateCount, setGenerateCount] = useState(100);

  const adminToken = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchSeedProfiles();
  }, [adminToken]);

  const fetchSeedProfiles = async () => {
    try {
      const response = await axios.get(`${API}/admin/seed-profiles`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSeedProfiles(response.data.profiles || []);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Error fetching seed profiles");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProfiles = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/generate-seed-profiles?count=${generateCount}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(response.data.message);
      fetchSeedProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error generating profiles");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteAllSeedProfiles = async () => {
    if (!window.confirm("Are you sure you want to delete ALL seed profiles? This action cannot be undone.")) {
      return;
    }
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/seed-profiles`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(response.data.message);
      setSeedProfiles([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error deleting profiles");
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const filteredProfiles = seedProfiles.filter(profile => 
    profile.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    profile.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    profile.seed_profile_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    profile.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const maleCount = seedProfiles.filter(p => p.gender === "Male").length;
  const femaleCount = seedProfiles.filter(p => p.gender === "Female").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-rose-500 font-serif text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-800 border-r border-slate-700 p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-serif text-lg font-bold text-white block">SingleSeen</span>
            <span className="text-rose-400 text-xs">Admin Panel</span>
          </div>
        </div>

        <nav className="space-y-2">
          <Link 
            to="/admin/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <TrendingUp className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/admin/reports"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <FileWarning className="w-5 h-5" />
            Reports
          </Link>
          <Link 
            to="/admin/users"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <Users className="w-5 h-5" />
            Users
          </Link>
          <Link 
            to="/admin/stories"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <Heart className="w-5 h-5" />
            Love Stories
          </Link>
          <Link 
            to="/admin/seed-profiles"
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-rose-500/10 text-rose-400 font-medium"
          >
            <Bot className="w-5 h-5" />
            Seed Profiles
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 sm:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-bold text-white mb-2">AI Seed Profiles</h1>
          <p className="text-slate-400">Manage AI-generated profiles for testing and demonstration</p>
        </div>

        {/* Warning Banner */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-500 font-medium">Admin Only Feature</p>
            <p className="text-amber-400/80 text-sm">These profiles are marked with a hidden flag (is_seed_profile: true) and are only visible in this admin section. Regular users will see them as normal profiles in their search results.</p>
          </div>
        </div>

        {/* Stats and Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Bot className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{seedProfiles.length}</p>
                <p className="text-slate-400 text-sm">Total Seed Profiles</p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <User className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{maleCount}</p>
                <p className="text-slate-400 text-sm">Male Profiles</p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center">
                <User className="w-6 h-6 text-pink-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{femaleCount}</p>
                <p className="text-slate-400 text-sm">Female Profiles</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={generateCount}
                  onChange={(e) => setGenerateCount(parseInt(e.target.value) || 100)}
                  className="bg-slate-700 border-slate-600 text-white w-20 h-8"
                  min={1}
                  max={500}
                />
                <span className="text-slate-400 text-sm">profiles</span>
              </div>
              <Button
                onClick={handleGenerateProfiles}
                disabled={generating}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Search and Actions Row */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, ID, or country..."
              className="bg-slate-800 border-slate-700 text-white pl-10"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchSeedProfiles}
              variant="outline"
              className="border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleDeleteAllSeedProfiles}
              disabled={deleting || seedProfiles.length === 0}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              {deleting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete All
            </Button>
          </div>
        </div>

        {/* Profiles Table */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-slate-400 font-medium">Seed ID</th>
                <th className="text-left p-4 text-slate-400 font-medium">Name</th>
                <th className="text-left p-4 text-slate-400 font-medium">Gender</th>
                <th className="text-left p-4 text-slate-400 font-medium">Age</th>
                <th className="text-left p-4 text-slate-400 font-medium">Location</th>
                <th className="text-left p-4 text-slate-400 font-medium">Muslim Type</th>
                <th className="text-left p-4 text-slate-400 font-medium">Style</th>
              </tr>
            </thead>
            <tbody>
              {filteredProfiles.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400">
                    {seedProfiles.length === 0 
                      ? "No seed profiles yet. Click 'Generate' to create AI profiles."
                      : "No profiles match your search."}
                  </td>
                </tr>
              ) : (
                filteredProfiles.slice(0, 50).map((profile) => (
                  <tr key={profile.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="p-4">
                      <span className="text-purple-400 font-mono text-sm">{profile.seed_profile_id}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-white">{profile.first_name} {profile.last_name}</span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        profile.gender === "Male" 
                          ? "bg-blue-500/20 text-blue-400" 
                          : "bg-pink-500/20 text-pink-400"
                      }`}>
                        {profile.gender}
                      </span>
                    </td>
                    <td className="p-4 text-slate-300">
                      {profile.date_of_birth 
                        ? new Date().getFullYear() - new Date(profile.date_of_birth).getFullYear()
                        : '-'}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 text-slate-300">
                        <MapPin className="w-3 h-3" />
                        <span className="text-sm">{profile.city}, {profile.country}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-emerald-400 text-sm">{profile.muslim_type}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm">{profile.fashion_style}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {filteredProfiles.length > 50 && (
            <div className="p-4 text-center text-slate-400 border-t border-slate-700">
              Showing 50 of {filteredProfiles.length} profiles. Use search to find specific profiles.
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminSeedProfiles;
